import { baseApi } from './baseApi';

const toQuery = (filter = {}) => {
  const p = new URLSearchParams();
  Object.entries(filter).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const leadsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listLeads: build.query({
      query: (filter) => `/leads${toQuery(filter)}`,
      transformResponse: (resp) => resp.data,
      // ALWAYS provide the LIST tag (not only when empty) — otherwise `invalidatesTags: LIST`
      // from create/delete mutations never re-fetches a non-empty list.
      providesTags: (result) => [
        { type: 'Lead', id: 'LIST' },
        ...(result?.items?.map((l) => ({ type: 'Lead', id: l.id })) ?? []),
      ],
    }),
    getLead: build.query({
      query: (id) => `/leads/${id}`,
      transformResponse: (resp) => resp.data,
      providesTags: (_r, _e, id) => [{ type: 'Lead', id }],
    }),
    unscheduledConsultations: build.query({
      query: () => '/leads/unscheduled-consultations',
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Lead', id: 'LIST' }],
    }),
    leadKanban: build.query({
      query: (assignedToUserId) => `/leads/kanban${assignedToUserId ? `?assignedToUserId=${assignedToUserId}` : ''}`,
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Lead', id: 'LIST' }],
    }),
    createLead: build.mutation({
      query: (body) => ({ url: '/leads', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'Lead', id: 'LIST' }, 'Dashboard'],
    }),
    // Bulk import from a parsed CSV/Excel upload — { items: [...] }.
    importLeads: build.mutation({
      query: (body) => ({ url: '/leads/import', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'Lead', id: 'LIST' }, 'Dashboard'],
    }),
    updateLead: build.mutation({
      query: ({ id, ...body }) => ({ url: `/leads/${id}`, method: 'PUT', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Lead', id }, { type: 'Lead', id: 'LIST' }],
    }),
    updateLeadStatus: build.mutation({
      query: ({ id, status, note }) => ({ url: `/leads/${id}/status`, method: 'PATCH', body: { status, note } }),
      transformResponse: (resp) => resp.data,
      // Also invalidate LeadFollowUp: marking a lead "Not Connected" auto-creates a follow-up
      // To-Do that must show up in Today's Tasks without a manual refresh.
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Lead', id }, { type: 'Lead', id: 'LIST' }, { type: 'LeadActivity', id },
        { type: 'LeadFollowUp', id: 'LIST' },
      ],
    }),
    updateLeadDepartment: build.mutation({
      query: ({ id, department }) => ({ url: `/leads/${id}/department`, method: 'PATCH', body: { department } }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Lead', id }, { type: 'Lead', id: 'LIST' }],
    }),
    assignLead: build.mutation({
      query: ({ id, assignedToUserId }) => ({ url: `/leads/${id}/assign`, method: 'PATCH', body: { assignedToUserId } }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Lead', id }, { type: 'Lead', id: 'LIST' }],
    }),
    classifyLead: build.mutation({
      query: ({ id, classification, reason }) => ({ url: `/leads/${id}/classify`, method: 'PATCH', body: { classification, reason } }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Lead', id }, { type: 'Lead', id: 'LIST' }],
    }),
    leadActivities: build.query({
      query: (id) => `/leads/${id}/activities`,
      transformResponse: (resp) => resp.data,
      providesTags: (_r, _e, id) => [{ type: 'LeadActivity', id }],
    }),
    addLeadActivity: build.mutation({
      query: ({ id, ...body }) => ({ url: `/leads/${id}/activities`, method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'LeadActivity', id }],
    }),
    // Callback promise ("contact after X") — powers the Today's Tasks engine.
    addLeadFollowUp: build.mutation({
      query: ({ id, ...body }) => ({ url: `/leads/${id}/followups`, method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LeadFollowUp', id: 'LIST' }, { type: 'LeadFollowUp', id },
        { type: 'LeadActivity', id }, { type: 'Lead', id: 'LIST' },
      ],
    }),
    completeLeadFollowUp: build.mutation({
      query: ({ id, followUpId }) => ({ url: `/leads/${id}/followups/${followUpId}/complete`, method: 'PATCH' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LeadFollowUp', id: 'LIST' }, { type: 'LeadFollowUp', id },
        { type: 'LeadActivity', id }, { type: 'Lead', id: 'LIST' },
      ],
    }),
    convertLead: build.mutation({
      query: ({ id, ...body }) => ({ url: `/leads/${id}/convert`, method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Lead', id }, { type: 'Lead', id: 'LIST' }, 'Patient', 'Dashboard'],
    }),
    deleteLead: build.mutation({
      query: (id) => ({ url: `/leads/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Lead', id: 'LIST' }],
    }),
  }),
});

export const {
  useListLeadsQuery,
  useGetLeadQuery,
  useUnscheduledConsultationsQuery,
  useLeadKanbanQuery,
  useCreateLeadMutation,
  useImportLeadsMutation,
  useUpdateLeadMutation,
  useUpdateLeadStatusMutation,
  useUpdateLeadDepartmentMutation,
  useAssignLeadMutation,
  useClassifyLeadMutation,
  useLeadActivitiesQuery,
  useAddLeadActivityMutation,
  useAddLeadFollowUpMutation,
  useCompleteLeadFollowUpMutation,
  useConvertLeadMutation,
  useDeleteLeadMutation,
} = leadsApi;
